/**
 * Test Playground — iframe wrapper around the production widget.
 *
 * Plan-PlaygroundIframeWidget.md §F. Replaces the prior ~660-line bespoke
 * playground (chat handler / attachments / voice mode / TTS / speech
 * recognition) with a thin shell that:
 *   1. Picks an agent.
 *   2. Renders `<iframe src="/widget/{agentId}?source=playground">` — the
 *      production widget runs inside, with `source=playground` driving
 *      backend's staff-auth recognition (Plan §B.2).
 *   3. Lists the staff's test history via
 *      `chatsAPI.getAgentPlaygroundHistory(agentSlug, visitorId)` — a
 *      bearer-authed, visitor-scoped, source-agnostic endpoint that works
 *      cross-site on staging (Plan-DecoupledToolCallAudit.md Slice 2). The
 *      old cookie-stamped `widgetAPI.getHistory({source:'playground'})` 401'd
 *      cross-site, leaving the sidebar (and the tool-calls chain) empty.
 *   4. Communicates with the iframe via `postMessage` — `load_session` to
 *      switch active session, `reset_visitor` to simulate a fresh
 *      customer.
 *
 * Bulk-delete still goes through the legacy `playgroundAPI.deleteHistory`
 * endpoint while Storm finalises Phase 3 cleanup; the endpoint still
 * exists post-Phase-2 so this is correct, just temporary.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { agentsAPI, chatsAPI, playgroundAPI, widgetAPI } from '../services/api';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useToast } from '../contexts/ToastContext';
import { Agent, ConversationComment } from '../types';
import {
  Bot,
  CheckSquare,
  Clock,
  History,
  Loader2,
  RefreshCw,
  Send,
  Square,
  Trash2,
  User,
  UserPlus,
  Wrench,
  X,
} from 'lucide-react';
import { formatServerDateTime } from '../utils/datetime';
import ToolCallPanel from '../components/Playground/ToolCallPanel';
import type { PlaygroundTrace } from '../utils/toolCalls';

/** Trace + history refresh cadence while the tool-call panel is open.
 *  NEAR-LIVE BY DESIGN, not streaming: the widget runs inside an iframe, so the
 *  parent cannot know when a turn ends without editing PROTECTED `Widget.tsx`.
 *  The owner explicitly accepted the poll instead. Paused when the tab is hidden
 *  and when the panel is closed, so an idle Playground costs nothing. */
const TRACE_POLL_MS = 5000;

interface PlaygroundSession {
  session_id: string;
  created_at: string;
  message_count: number;
  preview: string;
  // Plan-PlaygroundCollaboration §Backend-2 — attribution + comments.
  // Backend sends null for pre-fix (legacy) docs → render "Unknown / legacy".
  created_by_id?: string | null;
  created_by_name?: string | null;
  comments?: ConversationComment[];
}

const Playground: React.FC = () => {
  const { activeWorkspace } = useWorkspace();
  const { showToast } = useToast();

  // Deep-link from the workspace-wide `playground_comment` notification:
  // `/playground?agent=<slug>&session=<sid>` (Plan-PlaygroundCollaboration
  // §Backend-4 / §Frontend-3). `agent` is the agent SLUG, matching the
  // value used everywhere else here (agent selector + widget iframe URL).
  const [searchParams] = useSearchParams();
  const deepLinkAgent = searchParams.get('agent') ?? '';
  const deepLinkSession = searchParams.get('session') ?? '';

  const [agents, setAgents] = useState<Agent[]>([]);
  // Honour the deep-link agent immediately so the history fetch + iframe
  // target the right agent on first paint (before the agents list loads).
  const [selectedAgentId, setSelectedAgentId] = useState<string>(deepLinkAgent);
  const [iframeEpoch, setIframeEpoch] = useState<number>(0);
  // Open by default on desktop, but CLOSED on narrow screens — like the trace
  // panel below, the history sidebar renders as an overlay drawer on mobile and
  // would otherwise cover the widget on first paint (a static 288px column at
  // 375px leaves the iframe ~87px, effectively invisible).
  const [showHistory, setShowHistory] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  const [sessions, setSessions] = useState<PlaygroundSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState<boolean>(false);
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(new Set());
  const [activeSessionId, setActiveSessionId] = useState<string>('');
  const [commentText, setCommentText] = useState<string>('');
  const [addingComment, setAddingComment] = useState<boolean>(false);

  // Tool-call trace panel. Open by default on desktop (it is the instrument the
  // page exists for) but CLOSED on narrow screens, where it renders as an
  // overlay drawer and would otherwise cover the widget on first paint.
  const [showTrace, setShowTrace] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  const [trace, setTrace] = useState<PlaygroundTrace | null>(null);
  const [traceLoading, setTraceLoading] = useState<boolean>(false);
  const [traceUnavailable, setTraceUnavailable] = useState<boolean>(false);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  // A session the deep-link still needs to push into the iframe once it
  // has loaded (postMessage is lost if sent before the widget mounts).
  const pendingDeepLinkSession = useRef<string>(deepLinkSession);

  // Iframe URL — bump `epoch` on agent change / reset to force a reload.
  // `?source=playground` is the backend's signal (Plan §B.2) to look for
  // the staff `prodagi_session` cookie and tag the conversation
  // accordingly.
  //
  // Origin: `window.location.origin` (the dashboard host) — NOT
  // `VITE_API_URL` (the backend API host). The React SPA serving
  // `/widget/{agentId}` runs on the same origin as the dashboard. In
  // prod the dashboard is `app.prodagi.ai`; the API is `help.prodagi.ai`
  // — pre-fix, the iframe pointed at the API host which has no
  // `/widget/*` SPA route → 404. Using the dashboard origin keeps the
  // iframe same-origin with the parent page, which also means the
  // staff `prodagi_session` cookie flows naturally without
  // cross-subdomain `SameSite` considerations.
  //
  // API calls FROM the iframe (e.g. POST `/api/widget/chat`) still hit
  // `VITE_API_URL` per the iframe's own axios baseURL — cookie domain
  // `.prodagi.ai` covers both subdomains for the API roundtrip.
  const widgetUrl = useMemo(() => {
    if (!selectedAgentId) return '';
    return `${window.location.origin}/widget/${selectedAgentId}?source=playground&epoch=${iframeEpoch}`;
  }, [selectedAgentId, iframeEpoch]);

  // -- Data fetching --------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    agentsAPI
      .list()
      .then((res) => {
        if (cancelled) return;
        setAgents(res.data);
        if (res.data.length > 0 && !selectedAgentId) {
          setSelectedAgentId(res.data[0].agent_id);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch agents:', err);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** The stable per-browser visitor id every staff playground chat shares.
   *  The widget iframe is served from `window.location.origin` (same origin as
   *  this page), so its `prodagi_ai_visitor_id` localStorage entry is directly
   *  readable here — this is how the parent scopes the bearer-authed history
   *  query WITHOUT touching the protected `Widget.tsx`. Read fresh on each call
   *  so a poll picks up the id the moment the widget mints one (or a new one
   *  after "Reset Visitor"). Empty on a brand-new browser until the iframe
   *  writes it — handled as empty history. */
  const readVisitorId = (): string => {
    try {
      return localStorage.getItem('prodagi_ai_visitor_id') || '';
    } catch {
      return '';
    }
  };

  /** `quiet` — a background poll. Skips the loading flag so the sidebar spinner
   *  doesn't flash every few seconds while the trace panel is refreshing. */
  const fetchPlaygroundHistory = async (opts?: { quiet?: boolean }) => {
    if (!selectedAgentId) return;
    const visitorId = readVisitorId();
    if (!visitorId) {
      // The widget hasn't minted a visitor id yet (brand-new browser, first
      // paint). Nothing to list — the poll re-reads once the iframe writes it.
      setSessions([]);
      return;
    }
    if (!opts?.quiet) setSessionsLoading(true);
    try {
      // Plan-DecoupledToolCallAudit.md Slice 2 — the bearer-authed,
      // visitor-scoped, SOURCE-AGNOSTIC history endpoint. Replaces the old
      // cookie-stamped `widgetAPI.getHistory({source:'playground'})`, which
      // 401s cross-site on staging so the sidebar (and therefore the whole
      // tool-calls chain) stayed empty. `selectedAgentId` is the agent SLUG.
      const res = await chatsAPI.getAgentPlaygroundHistory(selectedAgentId, visitorId);
      const convos = res.data.conversations ?? [];
      setSessions(convos);
      // Deep-link: if the notification pointed at a specific session and
      // it's in this agent's history, open it (show the sidebar so the
      // comment thread is visible even for members who never use
      // Playground). The iframe push happens on its onLoad handler.
      const target = pendingDeepLinkSession.current;
      if (target && convos.some((c) => c.session_id === target)) {
        setActiveSessionId(target);
        setShowHistory(true);
      }
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number } };
      // The new endpoint is bearer + agent-ownership gated. A 4xx here means
      // Storm's Slice-2 backend isn't merged yet (or the caller doesn't own
      // this agent) — log quietly and show an empty list so the page doesn't
      // crash.
      if (ax.response?.status && ax.response.status >= 400) {
        console.warn(
          '[Playground] history endpoint returned ' + ax.response.status +
          ' — Storm Slice-2 backend may not be deployed yet',
        );
      } else {
        console.error('Failed to fetch playground history:', err);
      }
      setSessions([]);
    } finally {
      if (!opts?.quiet) setSessionsLoading(false);
    }
  };

  useEffect(() => {
    void fetchPlaygroundHistory();
    // Reset selection when agent / epoch changes — sessions belong to a
    // different (agent, time-window) and the bulk-delete set is stale.
    setSelectedSessionIds(new Set());
    setActiveSessionId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgentId, iframeEpoch]);

  // -- Tool-call trace ------------------------------------------------------
  //
  // 🚨 HOW WE KNOW WHICH SESSION TO TRACE, WITHOUT TOUCHING `Widget.tsx`.
  // When the tester chats live, the widget mints its own `session_id` INSIDE the
  // iframe — the parent is never told. Having the widget postMessage it out
  // would mean editing PROTECTED `Widget.tsx`, which this whole design exists to
  // avoid. Instead we discover it: the playground history endpoint sorts by
  // `last_message_at` DESC (verified in back/api/widget.py), so after any turn
  // the session being chatted in is `sessions[0]`. An explicitly opened session
  // (`activeSessionId`) always wins over that.
  //
  // ⚠️ The New Chat trap. A freshly minted session has NO messages, so it is not
  // in history yet — `sessions[0]` would still be the PREVIOUS conversation, and
  // the panel would show its tool calls as though they belonged to the new chat.
  // So on New Chat / Reset we remember the session that was on top and refuse to
  // fall back to it. The moment the new chat gets its first message it takes the
  // top spot under a different id, and the trace resolves on its own.
  const staleTopSessionRef = useRef<string>('');
  const topSessionId = sessions[0]?.session_id || '';
  const traceSessionId =
    activeSessionId ||
    (topSessionId && topSessionId !== staleTopSessionRef.current ? topSessionId : '');

  const fetchTrace = async (sessionId: string, opts?: { quiet?: boolean }) => {
    if (!sessionId) return;
    if (!opts?.quiet) setTraceLoading(true);
    try {
      // Plan-DecoupledToolCallAudit.md — the source-agnostic, bearer-authed
      // endpoint. Reads tool_calls for ANY conversation the org owns, so it
      // works for `source:None` chats (all of staging) where the old
      // cookie-stamped `getPlaygroundTrace` 404'd. `traceSessionId` still
      // decides WHICH session to query (New-Chat trap + poll unchanged).
      const res = await chatsAPI.getConversationToolCalls(sessionId);
      setTrace(res.data);
      setTraceUnavailable(false);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        // THE DENIAL CODE — the endpoint never returns 403 (that would confirm
        // the conversation exists). For an owned session this now essentially
        // never fires; when it does it means an unknown session or a
        // conversation this member's org doesn't own. Same UI meaning as
        // before: no trace. Degrade quietly — no toast, no console noise.
        setTrace(null);
        setTraceUnavailable(true);
      } else {
        console.error('Failed to fetch tool-call trace:', err);
        setTrace(null);
      }
    } finally {
      if (!opts?.quiet) setTraceLoading(false);
    }
  };

  // Load (and keep loading) the trace for the current target while the panel is
  // open. Near-live: we poll rather than stream, because the parent cannot see
  // into the iframe to know when a turn finished. The history poll is what makes
  // a BRAND-NEW live session appear as `sessions[0]` in the first place.
  useEffect(() => {
    if (!showTrace || !selectedAgentId) return;

    // Session changed → this is a fresh load, so show the spinner once.
    void fetchTrace(traceSessionId);

    const tick = () => {
      // Don't poll a backgrounded tab.
      if (document.hidden) return;
      void fetchPlaygroundHistory({ quiet: true });
      void fetchTrace(traceSessionId, { quiet: true });
    };
    const id = window.setInterval(tick, TRACE_POLL_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTrace, selectedAgentId, traceSessionId]);

  // Clear stale trace when the target disappears (agent switch / New Chat before
  // anything has been sent), so the panel never shows another session's calls.
  useEffect(() => {
    if (!traceSessionId) {
      setTrace(null);
      setTraceUnavailable(false);
    }
  }, [traceSessionId]);

  // A different agent has a different session list, so the New-Chat suppression
  // marker from the previous agent is meaningless — drop it, or the new agent's
  // most recent session would be wrongly withheld from the panel. Keyed on the
  // agent ONLY: keying it on `iframeEpoch` too would immediately undo the marker
  // that `startNewChat` just set (it bumps the epoch).
  useEffect(() => {
    staleTopSessionRef.current = '';
  }, [selectedAgentId]);

  // -- Iframe controls ------------------------------------------------------

  const loadSessionInIframe = (sessionId: string) => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'load_session', sessionId },
      '*',
    );
    setActiveSessionId(sessionId);
  };

  // -- Comments (mirrors ChatsTab "Internal Comments" UX) -------------------

  const activeSession = sessions.find((s) => s.session_id === activeSessionId);

  const handleAddComment = async () => {
    if (!activeSessionId || !selectedAgentId || !commentText.trim()) return;
    setAddingComment(true);
    try {
      const res = await widgetAPI.addPlaygroundComment(
        selectedAgentId,
        activeSessionId,
        commentText.trim(),
      );
      const newComment = res.data as ConversationComment;
      setCommentText('');
      // Optimistic append into the session's comments[] (same pattern as
      // ChatsTab L239-254).
      setSessions((prev) =>
        prev.map((s) =>
          s.session_id === activeSessionId
            ? { ...s, comments: [...(s.comments ?? []), newComment] }
            : s,
        ),
      );
      showToast('Comment added', 'success');
    } catch {
      showToast('Failed to add comment', 'error');
    } finally {
      setAddingComment(false);
    }
  };

  const resetVisitor = () => {
    // See the New Chat trap above — don't let the trace panel fall back to the
    // conversation we are walking away from.
    staleTopSessionRef.current = sessions[0]?.session_id || '';
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'reset_visitor' },
      '*',
    );
    // Bump epoch so the iframe key changes and React fully remounts the
    // iframe — `prodagi_widget_session` localStorage is iframe-origin
    // scoped, so clearing it from inside via the postMessage handler is
    // enough, but the remount also re-fires the widget config fetch.
    setIframeEpoch((e) => e + 1);
    setActiveSessionId('');
  };

  const startNewChat = () => {
    // Force a brand-new sessionId by reloading the iframe (the widget mints
    // a fresh sessionId on mount when no resumed session is found).
    // See the New Chat trap above — the fresh session has no messages yet, so
    // suppress the trace fallback until it earns the top spot itself.
    staleTopSessionRef.current = sessions[0]?.session_id || '';
    setIframeEpoch((e) => e + 1);
    setActiveSessionId('');
  };

  // -- Bulk delete ----------------------------------------------------------

  const allSelected =
    sessions.length > 0 && sessions.every((s) => selectedSessionIds.has(s.session_id));
  const someSelected = sessions.some((s) => selectedSessionIds.has(s.session_id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedSessionIds(new Set());
    } else {
      setSelectedSessionIds(new Set(sessions.map((s) => s.session_id)));
    }
  };

  const toggleSelectSession = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedSessionIds((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };

  const handleDeleteSessions = async () => {
    if (selectedSessionIds.size === 0) return;
    const ids = Array.from(selectedSessionIds);
    try {
      // Phase 2 keeps using the legacy playground delete endpoint — it
      // still exists pre-Phase-3 cleanup. Storm has not yet shipped a
      // widget-side delete with `source` filter (Plan §F.2 notes "need
      // to verify"). Swap to widgetAPI.delete when that lands.
      await playgroundAPI.deleteHistory(ids);
      showToast('Selected chats deleted successfully', 'success');
      if (selectedSessionIds.has(activeSessionId)) {
        startNewChat();
      }
      setSelectedSessionIds(new Set());
      void fetchPlaygroundHistory();
    } catch (err) {
      console.error('Failed to delete sessions:', err);
      showToast('Failed to delete chats', 'error');
    }
  };

  // -- Render ---------------------------------------------------------------

  if (!activeWorkspace) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-gray-500 dark:text-gray-400">
        Select a workspace to use the playground.
      </div>
    );
  }

  return (
    <div className="-m-6 flex flex-col overflow-hidden" style={{ height: 'calc(100dvh - 4rem)' }}>
      {/* ── Header ── */}
      <div className="flex-shrink-0 flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 glass">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-7 h-7 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <span className="text-sm font-semibold text-gray-900 dark:text-white truncate hidden sm:block">
            Test Playground
          </span>

          {/* Agent selector */}
          {agents.length > 0 && (
            <select
              value={selectedAgentId}
              onChange={(e) => {
                setSelectedAgentId(e.target.value);
                setIframeEpoch((x) => x + 1);
              }}
              className="ml-2 px-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label="Select agent to test"
            >
              {agents.map((a) => (
                <option key={a.id} value={a.agent_id}>
                  {a.config.agent_name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowHistory((v) => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            title={showHistory ? 'Hide history sidebar' : 'Show history sidebar'}
          >
            <History className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">History</span>
          </button>
          <button
            onClick={() => setShowTrace((v) => !v)}
            aria-pressed={showTrace}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              showTrace
                ? 'border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-200'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
            title={showTrace ? 'Hide the agent’s tool calls' : 'Show the agent’s tool calls'}
          >
            <Wrench className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Tool Calls</span>
          </button>
          <button
            onClick={resetVisitor}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            title="Clear visitor id + reload widget — simulates a new customer"
          >
            <UserPlus className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Reset Visitor</span>
          </button>
          <button
            onClick={startNewChat}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            New Chat
          </button>
        </div>
      </div>

      {/* ── Body: sidebar + iframe ── */}
      <div className="flex-1 min-h-0 flex">
        {/* ── History sidebar ──
            Same responsive treatment as the Tool-call panel below: a static
            288px column at lg+, but a fixed overlay drawer + backdrop below lg
            so it covers the widget (which keeps its full width) instead of
            crushing it. z-40 (drawer) / z-30 (backdrop) sit BELOW the app nav
            sidebar's z-50, so the logout escape hatch stays reachable. */}
        {showHistory && (
          <>
            <div
              className="lg:hidden fixed inset-0 z-30 bg-black/40"
              onClick={() => setShowHistory(false)}
              aria-hidden="true"
            />
            <aside
              className="
                fixed inset-y-0 left-0 z-40 w-[85vw] max-w-sm shadow-2xl
                lg:static lg:inset-auto lg:z-auto lg:w-72 lg:max-w-none lg:shadow-none
                flex-shrink-0 flex flex-col border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900
              "
              aria-label="Playground history"
            >
            <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={toggleSelectAll}
                className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                disabled={sessions.length === 0}
              >
                {allSelected ? (
                  <CheckSquare className="w-3.5 h-3.5 text-indigo-600" />
                ) : someSelected ? (
                  <CheckSquare className="w-3.5 h-3.5 text-indigo-600/50" />
                ) : (
                  <Square className="w-3.5 h-3.5" />
                )}
                <span>{allSelected ? 'Deselect All' : 'Select All'}</span>
              </button>
              <div className="flex items-center gap-2">
                {someSelected && (
                  <button
                    onClick={() => void handleDeleteSessions()}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900 hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete ({selectedSessionIds.size})
                  </button>
                )}
                <button
                  onClick={() => setShowHistory(false)}
                  className="lg:hidden p-1 -mr-1 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  aria-label="Close history"
                  title="Close history"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {sessionsLoading ? (
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 px-2 py-3">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Loading sessions…
                </div>
              ) : sessions.length === 0 ? (
                <p className="text-xs text-center text-gray-500 dark:text-gray-400 py-6 px-2">
                  No playground sessions yet. Send a message in the widget to
                  start one.
                </p>
              ) : (
                sessions.map((s) => {
                  const isSelected = selectedSessionIds.has(s.session_id);
                  const isActive = activeSessionId === s.session_id;
                  return (
                    <button
                      key={s.session_id}
                      onClick={() => loadSessionInIframe(s.session_id)}
                      className={`group w-full text-left p-2.5 rounded-lg transition-all relative ${
                        isActive
                          ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-900 dark:text-indigo-100'
                          : isSelected
                          ? 'bg-blue-50 dark:bg-blue-900/20'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <button
                        onClick={(e) => toggleSelectSession(s.session_id, e)}
                        className={`absolute top-2.5 left-2.5 z-10 transition-opacity ${
                          isSelected || someSelected
                            ? 'opacity-100'
                            : 'opacity-0 group-hover:opacity-100'
                        }`}
                        title={isSelected ? 'Deselect' : 'Select'}
                      >
                        {isSelected ? (
                          <CheckSquare className="w-3.5 h-3.5 text-indigo-600" />
                        ) : (
                          <Square className="w-3.5 h-3.5 text-gray-400" />
                        )}
                      </button>
                      <div className={someSelected ? 'pl-6' : ''}>
                        <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                          {s.preview || 'No message'}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatServerDateTime(s.created_at, undefined, {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                          <span>·</span>
                          <span>
                            {s.message_count} msg{s.message_count === 1 ? '' : 's'}
                          </span>
                        </div>
                        {/* Plan-PlaygroundCollaboration §Frontend-1 — all
                            workspace staff see every row; show who tested.
                            Backend sends null for pre-fix legacy docs. */}
                        <p className="flex items-center gap-1 mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 truncate">
                          <User className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">
                            Tested by {s.created_by_name || 'Unknown / legacy'}
                          </span>
                          {(s.comments?.length ?? 0) > 0 && (
                            <span className="ml-auto inline-flex items-center gap-0.5 px-1.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                              {s.comments?.length}
                            </span>
                          )}
                        </p>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Internal Comments — mirrors Inbox ChatsTab "Internal
                Comments" (ChatsTab.tsx L831+). Visible once a session is
                selected (incl. via the notification deep-link). */}
            {activeSession && (
              <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 flex flex-col max-h-[45%]">
                <p className="flex-shrink-0 px-3 pt-3 pb-2 text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Internal Comments
                </p>
                <div className="flex-1 overflow-y-auto px-3 space-y-2">
                  {(activeSession.comments?.length ?? 0) === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 italic pb-2">
                      No comments yet.
                    </p>
                  ) : (
                    activeSession.comments?.map((c: ConversationComment) => (
                      <div
                        key={c.comment_id}
                        className="bg-white dark:bg-gray-800 rounded-lg p-2.5 border border-gray-100 dark:border-gray-700 shadow-sm"
                      >
                        <div className="flex justify-between items-start gap-2 mb-1">
                          <span className="font-medium text-[11px] text-gray-900 dark:text-white truncate">
                            {c.author_name}
                          </span>
                          <span className="text-[10px] text-gray-400 flex-shrink-0">
                            {formatServerDateTime(c.created_at, [], {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            })}
                          </span>
                        </div>
                        <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                          {c.text}
                        </p>
                      </div>
                    ))
                  )}
                </div>
                <div className="flex-shrink-0 relative p-3">
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Add an internal note…"
                    className="w-full text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 pb-9 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 resize-none h-20 shadow-sm"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        void handleAddComment();
                      }
                    }}
                  />
                  <button
                    onClick={() => void handleAddComment()}
                    disabled={!commentText.trim() || addingComment}
                    className="absolute bottom-5 right-5 p-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors disabled:opacity-50"
                    title="Add comment"
                  >
                    {addingComment ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            )}
            </aside>
          </>
        )}

        {/* Iframe */}
        <div className="flex-1 min-w-0 bg-gray-100 dark:bg-gray-950 relative">
          {!selectedAgentId ? (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
              {agents.length === 0
                ? 'Create an agent first.'
                : 'Select an agent to start testing.'}
            </div>
          ) : (
            <iframe
              ref={iframeRef}
              key={widgetUrl}
              src={widgetUrl}
              className="w-full h-full border-0 block"
              // Plan §F.6 — `microphone` is required for voice mode; the
              // other allows mirror the customer-widget extension's
              // permission set (camera/autoplay/clipboard/fullscreen).
              allow="microphone; camera; autoplay; clipboard-write; fullscreen"
              title="Agent Test Widget"
              onLoad={() => {
                // Flush a deep-linked session into the widget once it has
                // mounted (postMessage before load is dropped). One-shot:
                // clear so later epoch bumps (New Chat / Reset) don't
                // reopen it.
                const target = pendingDeepLinkSession.current;
                if (target) {
                  pendingDeepLinkSession.current = '';
                  loadSessionInIframe(target);
                }
              }}
            />
          )}
        </div>

        {/* ── Tool-call panel ──
            Responsive by necessity, not decoration. The history sidebar is
            already 288px; a THIRD static column would crush the widget iframe
            into uselessness on a narrow screen. So:
              · lg and up  → a static right-hand column beside the iframe.
              · below lg   → a fixed overlay drawer + backdrop, so the iframe
                             keeps its full width and is simply covered while
                             the tester reads the trace.
            z-40 (drawer) / z-30 (backdrop) sit BELOW the app nav sidebar's
            z-50, so the nav — and the logout escape hatch — stay reachable. */}
        {showTrace && (
          <>
            <div
              className="lg:hidden fixed inset-0 z-30 bg-black/40"
              onClick={() => setShowTrace(false)}
              aria-hidden="true"
            />
            <aside
              className="
                fixed inset-y-0 right-0 z-40 w-[85vw] max-w-sm shadow-2xl
                lg:static lg:inset-auto lg:z-auto lg:w-80 xl:w-96 lg:max-w-none lg:shadow-none
                flex-shrink-0 border-l border-gray-200 dark:border-gray-700
              "
              aria-label="Agent tool calls"
            >
              <ToolCallPanel
                trace={trace}
                loading={traceLoading}
                unavailable={traceUnavailable}
                hasSession={Boolean(traceSessionId)}
                onRefresh={() => {
                  void fetchPlaygroundHistory({ quiet: true });
                  void fetchTrace(traceSessionId);
                }}
                onClose={() => setShowTrace(false)}
              />
            </aside>
          </>
        )}
      </div>
    </div>
  );
};

export default Playground;
